import React, { useRef, useState, useMemo } from "react";
import SimpleThemedButton from "../../../../genericComponents/simpleTextField/SimpleThemedButton";
import "./UploadContact.css";
import UploadContactFile from "./UploadContactFile";
import SearchOrAddDropdown from "./SearchOrAddDropdown";
import SimpleTextField from "../../../../genericComponents/simpleTextField/SimpleTextField";
import VerticalOrDivider from "./VerticalOrDivider";
import PreviewTable from "./PreviewTable";
import { addContactBulkAsync } from "../../../../../../services/iqApiV2";
import { useUser } from "../../../../../../context/UserContext";
import { useParams } from "react-router-dom";

function UploadContact({
  onBack,
  contacts,
  setContacts,
  setActiveTab,
  manuallyAddedContacts,
  setManuallyAddedContacts,
  handleProcessUploadContacts = async() => { }
}) {
  const [uploadProgress,setUploadProgress]=useState(false);
  const { user } = useUser();
  const { clientName, accountName } = useParams();
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({
    name: "",
    email: "",
    linkedin_url: "",
    caseType: "upload",
  });
  const [manualContact, setManualContact] = useState({
    name: "",
    email: "",
    linkedin_url: "",
  });

  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const previewWorkerRef = useRef(null);
  const processWorkerRef = useRef(null);

  // NEW: for click-to-upload inside dropzone
  const dropInputRef = useRef(null);

  // ---------- NEW: validators ----------
  const isValidEmail = (e = "") => {
    const v = e.trim();
    if (!v) return false;
    // simple & reliable email check
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v);
  };

  const isValidLinkedIn = (url = "") => {
    const v = url.trim();
    if (!v) return false;
    try {
      const u = new URL(v.startsWith("http") ? v : `https://${v}`);
      const hostOk = /(^|\.)linkedin\.com$/i.test(u.hostname);
      const pathOk = /^\/(in|company|pub)\//i.test(u.pathname); // allow common LI paths
      return hostOk && pathOk;
    } catch {
      return false;
    }
  };

  // ---------- NEW: derived validity for manual form ----------
  const nameOk = manualContact.name.trim().length > 0;
  const emailProvided = manualContact.email.trim().length > 0;
  const linkedinProvided = manualContact.linkedin_url.trim().length > 0;
  const emailOk = emailProvided ? isValidEmail(manualContact.email) : false;
  const linkedinOk = linkedinProvided ? isValidLinkedIn(manualContact.linkedin_url) : false;

  // Enable btn only if: name + (valid email OR valid linkedin)
  const canAddManual = useMemo(
    () => nameOk && (emailOk || linkedinOk),
    [nameOk, emailOk, linkedinOk]
  );

  const isCsv = (f) =>
    f &&
    (f.type === "text/csv" ||
      /\.csv$/i.test(f.name) ||
      f.name.toLowerCase().endsWith(".txt"));

  const isXlsx = (f) => f && /\.(xlsx|xlsm|xlsb|xls)$/i.test(f.name);

  // NEW: drag handlers
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    // Reuse existing handler
    handleFile({ target: { files: [f] } });
  };
  const openFilePicker = () => dropInputRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setFile(f);
    setHeaders([]);
    setPreviewData([]);
    setMapping({ name: "", email: "", linkedin_url: "", caseType: "upload" });
    setProgress(0);

    if (previewWorkerRef.current) {
      previewWorkerRef.current.terminate();
      previewWorkerRef.current = null;
    }

    setLoading(true);
    const worker = new Worker(new URL("./uploadWorker.js", import.meta.url), {
      type: "module",
    });
    previewWorkerRef.current = worker;

    worker.onmessage = (event) => {
      const { type } = event.data || {};
      if (type === "preview") {
        const { headers, rows } = event.data;
        setHeaders(headers || []);
        setPreviewData((rows || []).slice(0, 3));
        setLoading(false);
        worker.terminate();
        previewWorkerRef.current = null;
      } else if (type === "error") {
        console.error(event.data.message);
        setLoading(false);
        worker.terminate();
        previewWorkerRef.current = null;
      }
    };

    worker.onerror = (err) => {
      console.error("Preview worker error:", err);
      setLoading(false);
      worker.terminate();
      previewWorkerRef.current = null;
    };

    worker.postMessage({
      type: "preview",
      file: f,
      fileKind: isCsv(f) ? "csv" : isXlsx(f) ? "xlsx" : "unknown",
    });
  };

  const handleMappingChange = (key, value) => {
    setMapping((prev) => ({ ...prev, [key]: value }));
  };

  const mappingValid =
    Boolean(mapping.name) && Boolean(mapping.email || mapping.linkedin_url);

  const processDataInWorker = async () => {
    if (!file) return;

    if (contacts.length >= 50) {
      alert("You already have 50 contacts selected. Cannot add more.");
      return;
    }
    if (!mapping.name) {
      alert("Please map the Name field (required).");
      return;
    }
    if (!mapping.email && !mapping.linkedin_url) {
      alert("Please map at least one of: Email or LinkedIn URL (required).");
      return;
    }

    if (processWorkerRef.current) {
      processWorkerRef.current.terminate();
      processWorkerRef.current = null;
    }

    setLoading(true);
    setProgress(0);

    const worker = new Worker(new URL("./uploadWorker.js", import.meta.url), {
      type: "module",
    });
    processWorkerRef.current = worker;

    worker.onmessage = (event) => {
      const { type } = event.data || {};
      if (type === "progress") {
        setProgress(event.data.progress ?? 0);
      } else if (type === "done") {
        const { data } = event.data;
        // Replace previous data with new data instead of appending
        setContacts(data.slice(0, 50));
        setActiveTab("SELECTED");
        setLoading(false);
        setProgress(100);
        worker.terminate();
        processWorkerRef.current = null;
      } else if (type === "error") {
        console.error(event.data.message);
        setLoading(false);
        worker.terminate();
        processWorkerRef.current = null;
      }
    };

    worker.onerror = (err) => {
      console.error("Process worker error:", err);
      setLoading(false);
      worker.terminate();
      processWorkerRef.current = null;
    };

    worker.postMessage({
      type: "process",
      file,
      fileKind: isCsv(file) ? "csv" : isXlsx(file) ? "xlsx" : "unknown",
      mapping,
      options: { chunkSizeBytes: 2 * 1024 * 1024 },
    });
  };

  const abortProcessing = () => {
    if (processWorkerRef.current) {
      processWorkerRef.current.terminate();
      processWorkerRef.current = null;
      setLoading(false);
      setProgress(0);
    }
  };

  if (loading && !headers.length) {
    return (
      <div className="upload-loader-wrapper">
        <div className="spinner-border text-primary" role="status" />
        {progress > 0 && (
          <progress max={100} value={progress} style={{ width: 180 }} />
        )}
      </div>
    );
  }

  const FIELD_CONFIG = [
    { key: "name", label: "Name (required)" },
    { key: "email", label: "Email (optional)" },
    { key: "linkedin_url", label: "LinkedIn URL (optional)" },
  ];

  // ---------- NEW: manual add handler with validation ----------
  const handleAddManual = () => {
    const { name, email, linkedin_url } = manualContact;
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedLinkedIn = linkedin_url.trim();

    if (!trimmedName) {
      alert("Name is required.");
      return;
    }
    if (!trimmedEmail && !trimmedLinkedIn) {
      alert("Provide at least a valid Email or a valid LinkedIn URL.");
      return;
    }
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      alert("Please enter a valid email address.");
      return;
    }
    if (trimmedLinkedIn && !isValidLinkedIn(trimmedLinkedIn)) {
      alert("Please enter a valid LinkedIn profile/company URL.\nExample: https://www.linkedin.com/in/username");
      return;
    }
    if (contacts.length >= 50) {
      alert("Maximum 50 contacts allowed.");
      return;
    }

    setManuallyAddedContacts((prev) => [
      ...prev,
      {
        name: trimmedName,
        email: trimmedEmail,
        linkedin_url: trimmedLinkedIn,
        caseType: "manual",
        id: trimmedLinkedIn ? trimmedName + trimmedLinkedIn : trimmedName + trimmedEmail,
      },
    ]);

    setManualContact({ name: "", email: "", linkedin_url: "" });
  };

  // Handler for Upload Contact button
  const handleUploadContact = async () => {
    try {
      // Combine uploaded and manually added contacts
      const allContacts = [...contacts, ...manuallyAddedContacts];

      console.log("All Contacts before formatting:", allContacts);

      if (allContacts.length === 0) {
        alert("No contacts to upload. Please add contacts first.");
        return;
      }

      // Format contacts to match the API schema
      // Each contact needs: linkedin_url, user_id, client, account
      const formattedContacts = allContacts?.map(contact => {
        // Handle different possible field names for linkedin URL
        const linkedinUrl = contact?.linkedin_url || contact?.linkedinUrl || contact?.linkedin || "";
        const email = contact?.email || "";

        return {
          linkedin_url: linkedinUrl,
          email: email,
          user_id: user?.id,
          client: clientName,
          account: accountName
        };
      });

      const payload = {
        contacts: formattedContacts
      };

      console.log("Upload Contact Payload:", JSON.stringify(payload, null, 2));
      console.log("User ID:", user?.id);
      console.log("Client Name:", clientName);
      console.log("Account Name:", accountName);

      setUploadProgress(true)
      // Execute the original/previous functionality
     await handleProcessUploadContacts(payload);
      setUploadProgress(false)

    } catch (error) {
      console.error("Upload contact error details:", error);
      console.error("Error response:", error?.response);
      console.error("Error response data:", error?.response?.data);
      alert(`Failed to upload contacts: ${error?.response?.data?.detail || error.message || "Unknown error"}`);
    }
  };

  return (
    <div className="upload-contact-container">
      <span
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <div
          className="upload-dropzone"
          onDragOver={onDragOver}
          onDrop={onDrop}
          onClick={openFilePicker}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" ? openFilePicker() : null)}
          aria-label="Upload file by dragging and dropping or clicking to select"
        >
          <input
            ref={dropInputRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,.xlsb,.xls,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleFile}
            style={{ display: "none" }}
          />
          <div className="upload-dropzone-inner">
            <div className="upload-dropzone-icon" aria-hidden />
            <div className="upload-dropzone-text">
              <strong>Drag & drop</strong> your file here, or{" "}
              <span className="linklike">click to browse</span>
            </div>
            <div className="upload-dropzone-hint">
              CSV / XLSX • Name is required • Email or LinkedIn URL required
            </div>
          </div>
        </div>

        <VerticalOrDivider />

        <div className="manual-contact-form">
          <SimpleTextField
            label="Name *"
            placeholder="Enter full name"
            value={manualContact.name}
            setValue={(e) =>
              setManualContact((prev) => ({ ...prev, name: e.target.value }))
            }
          />

          <SimpleTextField
            label="Email"
            placeholder="example@domain.com"
            value={manualContact.email}
            setValue={(e) =>
              setManualContact((prev) => ({ ...prev, email: e.target.value }))
            }
          />
          {/* Optional inline hint */}
          {manualContact.email && !emailOk && (
            <div className="field-hint error">Enter a valid email</div>
          )}

          <SimpleTextField
            label="LinkedIn URL"
            placeholder="https://www.linkedin.com/in/username"
            value={manualContact.linkedin_url}
            setValue={(e) =>
              setManualContact((prev) => ({
                ...prev,
                linkedin_url: e.target.value,
              }))
            }
          />
          {/* Optional inline hint */}
          {manualContact.linkedin_url && !linkedinOk && (
            <div className="field-hint error">
              Enter a valid LinkedIn profile/company URL
            </div>
          )}

          <SimpleThemedButton
            buttonText="Add Contact"
            onBtnClick={handleAddManual}
            disabled={!canAddManual} // <-- only enabled when Name + (valid Email OR valid LinkedIn)
          />
        </div>
      </span>

      {headers.length > 0 && (
        <>
          <hr className="mapping-divider" />

          <div className="mapping-section">
            {FIELD_CONFIG.map(({ key, label }) => (
              <div className="mapping-row" key={key}>
                <label className="mapping-row-key">{label} :</label>
                <select
                  disabled={loading}
                  value={mapping[key] || ""}
                  onChange={(e) => handleMappingChange(key, e.target.value)}
                >
                  <option value="">Select column</option>
                  {headers.map((h, i) => (
                    <option key={i} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            {/* Process button moved here, next to LinkedIn URL */}
            <div className="mapping-row mapping-process-btn">
              {loading && processWorkerRef.current ? (
                <>
                  <div className="upload-progress" aria-live="polite">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    />
                    {progress > 0 && (
                      <>
                        <progress max={100} value={progress} />
                        <span className="upload-progress-text">
                          {Math.floor(progress)}%
                        </span>
                      </>
                    )}
                  </div>
                  <SimpleThemedButton
                    buttonText="Abort"
                    onBtnClick={abortProcessing}
                    variant="danger"
                  />
                </>
              ) : (
                <SimpleThemedButton
                  buttonText="Process"
                  onBtnClick={processDataInWorker}
                  disabled={!mappingValid || loading}
                />
              )}
            </div>
          </div>

          <hr className="mapping-divider" />
          <div className="preview">Preview</div>
          <PreviewTable headers={headers} previewData={previewData} />
          <hr className="mapping-divider" />

          {/* New bottom container with Upload Contact button */}
          <div className="upload-contact-footer">
            <SimpleThemedButton
              buttonText="Upload Contact"
              onBtnClick={handleUploadContact}
              isLoading={uploadProgress}
              btnDisabled={uploadProgress}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default UploadContact;
